/**
 * BankVerificationNotice — "finish your bank payment" card on the homeowner
 * portal's status and pay pages (Oct 20, 2026).
 *
 * A homeowner who types their bank account and routing numbers into Stripe
 * Checkout, instead of logging in to their bank, has to confirm a small test
 * deposit before Stripe takes the payment. They get 10 days; then Stripe
 * cancels it. These pages used to call that payment "processing — no action
 * needed", so nobody finished: 0 of 4 between May and October 2026.
 *
 * Data: ho_bank_verifications (migration 20261020c), behind the portal's deal
 * + last name + street number check. Each item carries Stripe's verify link.
 */

import { useEffect, useState } from "react";
import { authTriple, hoBankVerifications, type BankVerification } from "../../lib/homeownerRpc";
import {
  daysLeftLabel,
  daysUntil,
  depositInstructions,
  depositShouldBeThere,
  fmtVerifyDay,
  fmtVerifyDeadline,
} from "../../lib/bankVerification";

function fmtUSD(n: number | null | undefined) {
  if (n == null) return "$0.00";
  return "$" + Number(n).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

/**
 * The deal's bank payments still waiting on the homeowner's verification.
 * Re-checks when the page comes back into view (they verify in another tab
 * and return), and a few seconds after Stripe sends them back from paying,
 * since the webhook that records the payment can land just after the redirect.
 */
export function useBankVerifications(
  deal: { id: string; homeowner_name?: string | null; property_street?: string | null } | null | undefined,
): BankVerification[] {
  const [items, setItems] = useState<BankVerification[]>([]);

  useEffect(() => {
    if (!deal) {
      setItems([]);
      return;
    }
    let cancelled = false;
    const triple = authTriple(deal);
    const load = () => {
      hoBankVerifications(triple).then(
        ({ data, error }) => {
          if (cancelled || error) return;
          const list = (data as { verifications?: BankVerification[] } | null)?.verifications;
          setItems(Array.isArray(list) ? list : []);
        },
        () => { /* best-effort: the rest of the page still renders */ },
      );
    };
    load();

    const timers: number[] = [];
    if (new URLSearchParams(window.location.search).get("paid") === "1") {
      timers.push(window.setTimeout(load, 5_000), window.setTimeout(load, 15_000));
    }
    const onVisible = () => {
      if (document.visibilityState === "visible") load();
    };
    document.addEventListener("visibilitychange", onVisible);

    return () => {
      cancelled = true;
      timers.forEach((t) => window.clearTimeout(t));
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, [deal]);

  return items;
}

/** Total of the payments waiting on verification. */
export function verificationTotal(items: BankVerification[]): number {
  return items.reduce((s, v) => s + Number(v.amount ?? 0), 0);
}

export default function BankVerificationNotice({ items }: { items: BankVerification[] }) {
  if (items.length === 0) return null;
  return (
    <div className="space-y-3">
      {items.map((v, i) => (
        <NoticeCard key={`${v.started_at ?? ""}-${i}`} v={v} />
      ))}
    </div>
  );
}

function NoticeCard({ v }: { v: BankVerification }) {
  const { lookFor, enter } = depositInstructions(v.microdeposit_type);
  const arrival = fmtVerifyDay(v.deposit_arrival_at);
  const arrived = depositShouldBeThere(v.deposit_arrival_at);
  const deadline = fmtVerifyDeadline(v.verify_deadline_at);
  const left = daysLeftLabel(daysUntil(v.verify_deadline_at));

  return (
    <div className="rounded-2xl border-2 border-amber-300 bg-amber-50 p-5 shadow-sm">
      <div className="flex items-start gap-3">
        <span className="text-3xl leading-none" aria-hidden>🏦</span>
        <div className="min-w-0 flex-1">
          <h2 className="font-bold text-amber-900 text-lg leading-tight">
            Action needed: finish your {fmtUSD(v.amount)} bank payment
          </h2>
          <p className="text-sm text-amber-900 mt-2">
            You typed in your bank account and routing numbers, so Stripe has to confirm the account
            before the payment goes through. Nothing is charged until you do.
          </p>
          <p className="text-sm text-amber-900 mt-2">
            Look in your bank account for {lookFor}. Then tap below and enter {enter}.
            {arrival && !arrived && <> It should show up by {arrival}.</>}
          </p>
          {deadline && (
            <p className="text-sm text-amber-900 mt-2">
              <span className="font-semibold">
                Please finish by {deadline}{left ? ` (${left})` : ""}.
              </span>{" "}
              After that the payment is cancelled and you would need to pay again.
            </p>
          )}
          {v.verify_url ? (
            <a
              href={v.verify_url}
              target="_blank"
              rel="noopener noreferrer"
              className="mt-4 block w-full rounded-lg bg-amber-600 py-3 text-center font-bold text-white shadow-sm hover:bg-amber-700"
            >
              Verify my bank account →
            </a>
          ) : (
            <p className="mt-3 text-sm text-amber-900">
              We're getting the verification link from Stripe. Check back here in a few minutes, or
              call or text us at{" "}
              <a href="tel:+14708238816" className="font-semibold underline">(470) 823-8816</a>.
            </p>
          )}
          <p className="mt-3 text-xs text-amber-800">
            Questions? Call or text{" "}
            <a href="tel:+14708238816" className="font-medium underline">(470) 823-8816</a>.
          </p>
        </div>
      </div>
    </div>
  );
}
